import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useToast } from '../../components/admin/Toast';

interface EmailLog {
  id: string;
  templateKey: string;
  recipient: string;
  subject: string;
  status: 'sent' | 'failed';
  errorMessage: string | null;
  brevoMessageId: string | null;
  relatedOrderId: string | null;
  relatedUserId: string | null;
  createdAt: string;
}

interface EmailLogList {
  items: EmailLog[];
  total: number;
  page: number;
  totalPages: number;
}

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';

export default function AdminEmailLogsPage(): JSX.Element {
  const toast = useToast();
  const [data, setData] = useState<EmailLogList | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'' | 'sent' | 'failed'>('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (statusFilter) params.set('status', statusFilter);
      if (templateFilter) params.set('templateKey', templateFilter);
      const res = await api.get<EmailLogList>(`/admin/email-logs?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(extractError(err, 'Falha ao carregar histórico'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, templateFilter]);

  return (
    <>
      <SEO title="Emails — Histórico" noindex />
      <div className="mb-2 text-sm">
        <Link to="/admin/emails" className="text-admin-primary hover:underline">
          ← Templates
        </Link>
      </div>
      <h1>Histórico de envios</h1>
      <p className="mt-1 mb-4 text-admin-text-soft">
        Cada tentativa de envio (sucesso ou falha) é registada aqui para diagnóstico.
      </p>

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as '' | 'sent' | 'failed');
            setPage(1);
          }}
          className={inputClass}
        >
          <option value="">Todos os estados</option>
          <option value="sent">Enviados</option>
          <option value="failed">Falhados</option>
        </select>
        <input
          value={templateFilter}
          onChange={(e) => {
            setTemplateFilter(e.target.value);
            setPage(1);
          }}
          placeholder="Filtrar por template (ex.: order_confirmed)"
          className={inputClass}
        />
      </div>

      {loading ? (
        <p className="text-admin-text-soft">A carregar…</p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-admin-text-soft">Sem registos.</p>
      ) : (
        <>
          <table className="w-full overflow-hidden rounded-lg bg-admin-surface shadow-sm">
            <thead>
              <tr className="border-b border-admin-border">
                <th className={thClass}>Data</th>
                <th className={thClass}>Template</th>
                <th className={thClass}>Destinatário</th>
                <th className={thClass}>Assunto</th>
                <th className={thClass}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <Fragment key={row.id}>
                  <tr
                    className="cursor-pointer border-b border-admin-border last:border-b-0 hover:bg-admin-surface-alt"
                    onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                  >
                    <td className="p-3 text-xs text-admin-text-soft">
                      {formatDate(row.createdAt)}
                    </td>
                    <td className="p-3 text-xs">
                      <code>{row.templateKey}</code>
                    </td>
                    <td className="p-3 text-xs">{row.recipient}</td>
                    <td className="p-3 text-xs">{row.subject}</td>
                    <td className="p-3 text-xs">
                      {row.status === 'sent' ? (
                        <span className="rounded-full bg-admin-cream px-2 py-0.5 text-admin-primary">
                          Enviado
                        </span>
                      ) : (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-admin-danger">
                          Falha
                        </span>
                      )}
                    </td>
                  </tr>
                  {expanded === row.id && (row.errorMessage || row.brevoMessageId) ? (
                    <tr className="border-b border-admin-border">
                      <td colSpan={5} className="bg-admin-surface-alt p-3 text-xs">
                        {row.errorMessage ? (
                          <p className="text-admin-danger">
                            <strong>Erro:</strong> {row.errorMessage}
                          </p>
                        ) : null}
                        {row.brevoMessageId ? (
                          <p className="text-admin-text-soft">
                            <strong>Brevo Message ID:</strong> {row.brevoMessageId}
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>

          <div className="mt-4 flex items-center gap-3 text-sm">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-md border border-admin-border px-3 py-1 disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-admin-text-soft">
              Página {data.page} de {data.totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page >= data.totalPages}
              className="rounded-md border border-admin-border px-3 py-1 disabled:opacity-40"
            >
              Seguinte
            </button>
          </div>
        </>
      )}
    </>
  );
}
